import { useState, useCallback } from "react";
import {
    ITreeMenu,
    useTranslate,
    useLogout,
    useWarnAboutChange,
    useIsAuthenticated,
    useGetIdentity,
    useApiUrl,
} from "@refinedev/core";
import { Link, useLocation } from "react-router-dom";
import { Layout as AntdLayout, Menu, Grid, MenuProps } from "antd";
import {
    HomeOutlined,
    LogoutOutlined,
    UnorderedListOutlined,
} from "@ant-design/icons";
import { AiOutlineBlock, AiOutlineBuild, AiOutlineCloudServer, AiOutlineProduct } from "react-icons/ai";
import { IoMdArrowRoundBack } from "react-icons/io";
import { antLayoutSider, antLayoutSiderMobile, logoStyle } from "./styles";

import logo from "assets/logo-150.png";
import saintsburg from "assets/saintsburg-transparant.png";

type MenuItem = Required<MenuProps>['items'][number];

export const CMSSider = ({ setSiderKey }: { setSiderKey: (key: string) => void }) => {
    const [collapsed, setCollapsed] = useState<boolean>(false);
    const [openKeys, setOpenKeys] = useState<string[]>([]);

    const { data: isAuthenticated } = useIsAuthenticated();
    const { warnWhen, setWarnWhen } = useWarnAboutChange();
    const { mutate: mutateLogout } = useLogout();
    const translate = useTranslate();

    const { data: identity } = useGetIdentity();
    const identity_roles = identity as string[];

    const breakpoint = Grid.useBreakpoint();
    const isMobile = typeof breakpoint.lg === "undefined" ? false : !breakpoint.lg;

    const location = useLocation();
    const currentPath = location.pathname;

    const menuItems: ITreeMenu[] = [
        {
            name: "home",
            list: "/home",
            meta: {
                icon: <IoMdArrowRoundBack style={{marginTop: 2}}/>,
                label: "Back",
                hide: true,
            },
            children: []
        },
        {
            name: "dashboard",
            list: "/cms",
            meta: {
                icon: <AiOutlineProduct />,
                label: "Dashboard",
                hide: true,
            },
            children: []
        },
        {
            name: "content",
            list: "/cms/content",
            meta: {
                icon: <AiOutlineCloudServer />,
                label: "Content",
                hide: true,
            },
            children: []
        },
        // {
        //     name: "structures",
        //     list: "/cms/structures",
        //     meta: {
        //         icon: <AiOutlineBuild />,
        //         label: "Structures",
        //         hide: true,
        //     },
        //     children: []
        // },
        // {
        //     name: "templates",
        //     list: "/cms/templates",
        //     meta: {
        //         icon: <AiOutlineBlock />,
        //         label: "Templates",
        //         hide: true,
        //     },
        //     children: []
        // },
    ]

    const onOpenChange = useCallback((keys: string[]) => {
        const latestOpenKey = keys.find(key => !openKeys.includes(key));

        if (latestOpenKey) {
            const rootKey = latestOpenKey.split('/').slice(0, -1).join('/');
            const newOpenKeys = keys.filter(key => {
                return key === rootKey || key === latestOpenKey;
            });
            setOpenKeys(newOpenKeys);
        } else {
            setOpenKeys(keys);
        }
    }, [openKeys]);

    const handleReturnHome = () => {
        setSiderKey("main");
    }

    const renderTreeView = (tree: ITreeMenu[], selectedKey: string): MenuItem[] => {
        return tree.map((item: ITreeMenu) => {
            if (!isAuthenticated?.authenticated || !identity_roles.includes("admin")) return null;

            const { name, list, children, meta } = item;
            const route = list?.toString() ?? name;

            if (name === "home") {
                return {
                    key: route,
                    icon: meta?.icon ?? <UnorderedListOutlined />,
                    label: meta?.label,
                    onClick: handleReturnHome
                };
            }

            const isRoute = !(
                meta?.parent !== undefined &&
                children.length === 0
            );
            return {
                key: route,
                icon: meta?.icon ?? (isRoute && <UnorderedListOutlined />),
                label: route ? <Link to={route}>{meta?.label}</Link> : meta?.label
            };
        }).filter(Boolean) as MenuItem[];
    };

    const handleLogout = () => {
        if (warnWhen) {
            const confirm = window.confirm(
                translate(
                    "warnWhenUnsavedChanges",
                    "Are you sure you want to leave? You have unsaved changes.",
                ),
            );

            if (confirm) {
                setWarnWhen(false);
                mutateLogout();
            }
        } else {
            mutateLogout();
        }
    };

    const renderSider = () => {
        const loginLogout = isAuthenticated?.authenticated ?
            ({
                key: "logout",
                onClick: handleLogout,
                icon: <LogoutOutlined />,
                label: translate("buttons.logout", "Logout")
            }) : ({
                key: "login",
                icon: <LogoutOutlined />,
                label: <Link to="/login">{translate("buttons.login", "Login")}</Link>
            });
        var items = renderTreeView(menuItems, currentPath);
        var extended = items.concat(loginLogout);
        return extended;
    };

    var siderItems = renderSider();

    return (
        <AntdLayout.Sider
            collapsible
            collapsedWidth={isMobile ? 0 : 80}
            collapsed={collapsed}
            breakpoint="lg"
            onCollapse={(collapsed: boolean): void => setCollapsed(collapsed)}
            style={isMobile ? antLayoutSiderMobile : antLayoutSider}
        >
            <Link to="/">
                {collapsed ? (
                    <img
                        src={logo}
                        alt="Saintsburg"
                        style={logoStyle}
                    />
                ) : (
                    <img
                        src={saintsburg}
                        alt="Saintsburg"
                        style={logoStyle}
                    />
                )}
            </Link>
            <Menu
                theme="dark"
                defaultOpenKeys={["/cms"]}
                selectedKeys={[currentPath]}
                mode="inline"
                items={siderItems}
                openKeys={openKeys}
                onOpenChange={onOpenChange}
                onClick={() => {
                    if (!breakpoint.lg) {
                        setCollapsed(true);
                    }
                }}
            />
        </AntdLayout.Sider>
    );
};